import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi, segmentsApi, termsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card,
  CardContent,
  CardHeader,
  CardDescription
} from '@/components/ui/card'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import {
  Play,
  Download,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  Loader2,
  BookOpen,
  FileText,
  Info,
  MessageSquare,
  FileDown,
  Send,
  Upload,
  Square,
  Edit,
  Link2,
  Unlink,
  Search,
  Eye,
  User
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import * as Diff from 'diff'
import { useVirtualizer } from '@tanstack/react-virtual'
import { SegmentEditor } from '../components/SegmentEditor'
import { TermHighlighter } from '../components/TermHighlighter'
import { RAGHintPopup } from '../components/RAGHintPopup'
import { ReferenceInfoTable } from '../components/ReferenceInfoTable'
import { ReferenceMatchConfirmCard } from '../components/ReferenceMatchConfirmCard'
import { TranslatorAssignment } from '../components/TranslatorAssignment'
import { TermReviewPanel } from '../components/TermReviewPanel'
import { TermSuggestionPopover } from '../components/TermSuggestionPopover'
import { SearchReplacePanel } from '../components/SearchReplacePanel'
import { ConcordancePanel } from '../components/ConcordancePanel'
import { formatLanguagePair, formatDate } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface Project {
  id: string
  clientId: string
  fileName: string
  status: string
  sourceLanguage: string
  targetLanguage: string
  rawWordCount: number
  sourceWordCount?: number | null
  targetWordCount?: number | null
  deadline?: string
  errorMessage?: string
  fileFormat: string
  documentType?: string
  dramaTaskType?: string
  referenceConfirmed?: boolean
  referenceMatchesConfirmed?: boolean
  referenceFiles?: Array<{
    id: string
    fileName: string
    parsedContent?: string
  }>
  client?: {
    name: string
    code: string
    billingType?: 'source' | 'target'
    enableTM?: boolean
  }
  translator?: {
    name: string
  } | null
}

interface Segment {
  id: string
  projectId: string
  sequenceNumber: number
  sourceText: string
  translation?: string
  previousTranslation?: string
  revisionNote?: string
  confidenceScore?: number
  mqmScores?: any
  aiNote?: string
  isConfirmed: boolean
  isLocked: boolean
  syncDisabled: boolean
  updatedAt: string
  formatInfo?: string
}

interface Term {
  source: string;
  target: string;
  context?: string;
}

/**
 * 人物名行判定（短剧）：居中 + 剥离括号后 ≤12 字 + 全段无标点。
 * 与 python-engine/app/core/utils.py 的 is_character_name_line 及
 * apps/api/src/utils/text.ts 的 isCharacterNameLine 算法保持一致，三端修改必须同步。
 * （web 端括号剥离用单层正则即可——人物名行括号提示不嵌套，嵌套场景由双端完整算法兜底）
 */
function isCharacterNameLine(text: string | null | undefined, alignment: string | null | undefined): boolean {
  if (!String(alignment || '').toUpperCase().includes('CENTER')) return false
  const s = (text || '').replace(/\{\s*\/?\d+\s*\}/g, '').replace(/[（(][^（）()]*[)）]/g, '').trim()
  if (!s || s.length > 12) return false
  if (/[。！？!?…，,、；;：:.]/.test(s)) return false
  return true
}

/**
 * 人物名显示清洗：去格式 tag + 去括号提示 + 压缩空白（「李明（怒）」→「李明」）。
 * 与双端 cleanCharacterName 保持一致。
 */
function cleanCharacterName(text: string | null | undefined): string {
  return (text || '').replace(/\{\s*\/?\d+\s*\}/g, '').replace(/[（(][^（）()]*[)）]/g, '').replace(/\s+/g, ' ').trim()
}

/**
 * 从段落的 formatInfo JSON（Engine parse 时写入）解析对齐方式（如 "CENTER"）。
 * 解析失败或无 formatInfo 时返回空串（视为非居中段）。
 */
function segFormatAlignment(seg: Segment): string {
  if (!seg.formatInfo) return ''
  try {
    const format = JSON.parse(seg.formatInfo)
    return (format && typeof format === 'object' && format.alignment) || ''
  } catch {
    return ''
  }
}

function SegmentRow({
  seg,
  index,
  projectId,
  projectStatus,
  shouldFocus,
  onConfirmSuccess,
  onFocusHandled,
  terms,
  onRAGHint,
  rowRef,
  dataIndex,
  globalInsertSignal,
  onInsertSignalConsumed,
  onEditingChange,
  hasDuplicate,
  characterName
}: {
  seg: Segment,
  index: number,
  projectId: string,
  projectStatus: string,
  shouldFocus?: boolean,
  onConfirmSuccess?: (index: number) => void,
  onFocusHandled?: () => void,
  terms?: Term[],
  onRAGHint?: (state: { visible: boolean; sourceText: string; position: { x: number; y: number } }) => void
  rowRef?: (node: HTMLTableRowElement | null) => void,
  dataIndex?: number,
  /** 全局插入信号：来自 Concordance 面板，segmentId 匹配本段时触发插入 */
  globalInsertSignal?: { segmentId: string; text: string; nonce: number } | null,
  /** 本段消费完 globalInsertSignal 后回调，父组件据此清空信号避免重复触发 */
  onInsertSignalConsumed?: () => void,
  /** 本段编辑态变化时上报 segmentId（编辑中）或 null（退出编辑） */
  onEditingChange?: (segmentId: string | null) => void
  /** 本段原文在项目内是否存在完全重复的其他段落（决定是否显示重复同步标记） */
  hasDuplicate?: boolean
  /** 短剧：本段台词所属角色名（取自上方最近的人物名行，人物名行本身不显示） */
  characterName?: string
}) {
  const queryClient = useQueryClient()
  const [localTranslation, setLocalTranslation] = useState(seg.translation || '')
  const [isEditing, setIsEditing] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 用 ref 缓存最新的译文值，避免 blur/onConfirm 闭包读到旧 state
  // 注意：ref 必须在 onChange 里同步更新，不能用 useEffect（useEffect 也是渲染后异步执行的，同样有竞态）
  const localTranslationRef = useRef(localTranslation)
  // 防止 blur 和 onConfirm 重复触发保存（Mod-Enter 确认时会先触发 blur 再触发 onConfirm）
  const saveHandledRef = useRef(false)
  // 本段插入信号：术语浮层点击"插入到译文"时设置。已编辑→直接 nonce 触发；未编辑→进编辑态用 initialInsert
  const [localInsertSignal, setLocalInsertSignal] = useState<{ text: string; nonce: number } | null>(null)
  // 未编辑态点插入时暂存文本，等 SegmentEditor mount 后通过 initialInsert 一次性插入
  const [pendingInitialInsert, setPendingInitialInsert] = useState<string | null>(null)
  // TM 100% 段级精确匹配标记：后端 start-translation 对该类段落写入 is_locked=true + 预填译文。
  // 用于在译文区显著区分"翻译记忆(TM)匹配"与"机翻"，避免译者混淆。
  // 必须加"有译文"条件：isLocked 还被短剧仅台词的非台词段（无译文）和手动锁定段复用，
  // 口径与后端 nutstore isFullMatchSegment 一致，否则非 TM 锁定段会误挂 TM 徽章
  const isTmMatch = seg.isLocked === true && !!seg.translation

  // onChange 回调：同步更新 ref + 异步更新 state
  // SegmentEditor 的 dispatchTransaction 调用 onChange 是同步的，
  // 所以 ref.current 在 blur 触发时一定是最新的
  const handleTranslationChange = (value: string) => {
    localTranslationRef.current = value  // 同步更新 ref，确保 blur 时能读到最新值
    setLocalTranslation(value)           // 异步更新 state，触发 UI 重新渲染
  }

  useEffect(() => {
    if (!isEditing) {
      localTranslationRef.current = seg.translation || ''  // 同步 ref
      setLocalTranslation(seg.translation || '')
    }
  }, [seg.translation, isEditing])

  // 编辑态变化时上报：供 ProjectDetail 跟踪当前正在编辑的段落，Concordance 据此判断可否插入
  useEffect(() => {
    onEditingChange?.(isEditing ? seg.id : null)
  }, [isEditing, seg.id, onEditingChange])

  /**
   * 术语浮层"插入到译文"回调：
   * - 已编辑：直接设置 localInsertSignal，nonce 变化触发 SegmentEditor 插入到当前光标位置
   * - 未编辑：进入编辑态 + 暂存 pendingInitialInsert，SegmentEditor mount 后通过 initialInsert 插入到末尾
   */
  const handleInsertTargetToTranslation = (target: string) => {
    if (isEditing) {
      setLocalInsertSignal({ text: target, nonce: Date.now() })
    } else {
      setPendingInitialInsert(target)
      saveHandledRef.current = false
      setIsEditing(true)
    }
  }

  /** 合并后的插入信号传给 SegmentEditor：优先本段 local，否则全局（过滤 segmentId 匹配） */
  const effectiveInsertSignal = localInsertSignal ?? (
    globalInsertSignal && globalInsertSignal.segmentId === seg.id
      ? { text: globalInsertSignal.text, nonce: globalInsertSignal.nonce }
      : null
  )

  useEffect(() => {
    if (shouldFocus) {
      saveHandledRef.current = false  // 进入编辑模式时重置保存标记
      setIsEditing(true)
      onFocusHandled?.()
      setTimeout(() => {
        const el = document.getElementById(`segment-${seg.id}`)
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
  }, [shouldFocus, onFocusHandled, seg.id])

  const updateMutation = useMutation({
    mutationFn: (data: { translation: string; isLocked?: boolean }) => segmentsApi.update(seg.id, data),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      // 后端联动同步了相同原文段落时，toast 提示译者
      const syncedCount = (res as any)?.syncedCount || 0
      if (syncedCount > 0) {
        toast.success(`已同步到 ${syncedCount} 处相同原文`)
      }
    }
  })

  const confirmMutation = useMutation({
    mutationFn: () => segmentsApi.confirm(seg.id),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      // 后端联动确认了译文一致的重复段落时，toast 提示译者
      const syncedCount = (res as any)?.syncedCount || 0
      if (syncedCount > 0) {
        toast.success(`已确认（含 ${syncedCount} 处相同段落）`)
      } else {
        toast.success('已确认')
      }
      onConfirmSuccess?.(index)
    }
  })

  // 锁定/解锁段落
  const lockMutation = useMutation({
    mutationFn: (locked: boolean) => segmentsApi.update(seg.id, { isLocked: locked }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      toast.success(seg.isLocked ? '已解锁' : '已锁定')
    }
  })

  // 切换"不跟进重复原文同步"开关
  const syncToggleMutation = useMutation({
    mutationFn: () => segmentsApi.syncToggle(seg.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      toast.success(seg.syncDisabled ? '已开启重复原文同步' : '已标记本段不跟进同步')
    }
  })

  // 构建保存译文的 payload：如果段落当前是锁定的，同时解锁（人工修改优先于 TM 锁定）
  const buildTranslationPayload = (translation: string): { translation: string; isLocked?: boolean } => {
    if (seg.isLocked) {
      return { translation, isLocked: false }
    }
    return { translation }
  }

  // 统一的保存函数：读取 ref 中的最新译文，避免闭包读到旧 state
  // 用 saveHandledRef 防止同一次交互（blur + onConfirm）重复保存
  const flushSave = () => {
    if (saveHandledRef.current) return
    saveHandledRef.current = true
    const latest = localTranslationRef.current
    const original = seg.translation || ''
    console.debug('[Segments] flushSave', {
      segmentId: seg.id,
      latestLen: latest.length,
      originalLen: original.length,
      changed: latest !== original,
      latestPreview: latest.slice(0, 50),
      originalPreview: original.slice(0, 50),
    })
    if (latest !== original) {
      updateMutation.mutate(buildTranslationPayload(latest), {
        onError: (err) => {
          console.error('[Segments] 保存译文失败', { segmentId: seg.id, error: err })
          toast.error('保存失败：' + (err as any)?.message || '未知错误')
        }
      })
    }
  }

  const handleBlur = () => {
    setIsEditing(false)
    flushSave()
  }

  // 格式化文本显示，支持两类行内格式标签：
  //   1. DOCX/PPTX 行内标签：{1}、{/1}（数字）
  //   2. ASS 字幕标签：{\an8}、{\b1}、{\i1}、{\c&HFFFFFF&} 等（反斜杠开头）
  // 两类标签都渲染为不可编辑的 Badge，避免译者误删
  const renderTextWithTags = (text: string, _formatInfo?: any) => {
    if (!text) return text;

    // 解析行内标签：{数字}、{/数字} 或 {\xxx}
    const parts = text.split(/(\{\/?\d+\}|\{\\[^}]+\})/g);

    return (
      <span className="leading-relaxed">
        {parts.map((part, index) => {
          const match = part.match(/^\{(\/?\d+|\\[^}]+)\}$/);
          if (match) {
            const label = match[1];
            return (
              <Badge
                key={index}
                variant="outline"
                className="text-[10px] px-1 h-4 min-w-[18px] justify-center font-mono bg-warning/10 text-warning border-warning/30 mx-0.5 select-none inline-flex"
              >
                {label}
              </Badge>
            );
          }
          return <span key={index} className="whitespace-pre-wrap">{part}</span>;
        })}
      </span>
    );
  };

  // 渲染修订差异（inline 模式，直接嵌入译文区，类似 Word 修订）
  const renderRevisionDiff = (oldText: string, newText: string) => {
    if (!oldText || !newText) return renderTextWithTags(oldText || newText);

    // 使用 diffWords 进行词级比较
    const diff = Diff.diffWords(oldText, newText);

    return (
      <span className="leading-relaxed">
        {diff.map((part, index) => {
          if (part.added) {
            return (
              <span key={index} className="bg-success/15 text-success-foreground px-0.5 rounded whitespace-pre-wrap">
                {part.value}
              </span>
            );
          }
          if (part.removed) {
            return (
              <span key={index} className="text-destructive line-through opacity-60 whitespace-pre-wrap">
                {part.value}
              </span>
            );
          }
          return (
            <span key={index} className="whitespace-pre-wrap">
              {part.value}
            </span>
          );
        })}
      </span>
    );
  };

  // 判断是否可编辑（项目状态允许即可，锁定段落也允许编辑——人工修改优先于 TM 锁定）
  const canEdit = ['translating', 'reviewing', 'qc', 'error'].includes(projectStatus)

  const handleMouseEnter = (e: React.MouseEvent) => {
    hoverTimeoutRef.current = setTimeout(() => {
      onRAGHint?.({
        visible: true,
        sourceText: seg.sourceText,
        position: { x: e.clientX, y: e.clientY },
      })
    }, 500)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    onRAGHint?.({ visible: false, sourceText: '', position: { x: 0, y: 0 } })
  }

  return (
    <TableRow
      ref={rowRef}
      data-index={dataIndex}
      id={`segment-${seg.id}`}
      // 底色优先级：已确认（绿）> TM 匹配（蓝）。两个类同时挂时谁生效取决于
      // Tailwind CSS 生成顺序而非业务状态，曾导致"已确认 TM 段"显示蓝底、
      // "已确认普通段"显示绿底的状态不一致——改为互斥，确认态一律绿底
      className={`group transition-colors ${seg.isConfirmed ? 'bg-success/5' : isTmMatch ? 'bg-primary/5' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TableCell className="w-[60px] text-[10px] text-muted-foreground pl-6 align-top pt-5">
        <div className="flex items-center gap-1">
          {index + 1}
          {seg.isLocked && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                      <span className="text-muted-foreground/70">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-[10px]">此段落已锁定</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
      <TableCell className="w-[45%] py-4 align-top">
        <div className={`text-base leading-relaxed whitespace-pre-wrap ${seg.isLocked ? 'text-muted-foreground' : ''}`}>
          {terms && terms.length > 0 ? (
            <TermHighlighter
              text={seg.sourceText}
              terms={terms}
              onInsertTarget={!seg.isLocked ? handleInsertTargetToTranslation : undefined}
            />
          ) : (
            renderTextWithTags(seg.sourceText, seg.formatInfo)
          )}
        </div>
      </TableCell>
      <TableCell className="w-[45%] py-4 align-top">
        {projectStatus === 'translating' && !seg.translation ? (
          <div className="flex items-center gap-2 text-muted-foreground italic text-sm">
            <Loader2 className="h-3 w-3 animate-spin" /> 正在翻译...
          </div>
        ) : (
          <div className="space-y-2">
            {isEditing && canEdit ? (
              <SegmentEditor
                value={localTranslation}
                sourceText={seg.sourceText}
                onChange={handleTranslationChange}
                onConfirm={() => {
                  // 编辑器内确认（Mod-Enter）：先保存修改（读 ref 拿最新值），再确认段落
                  // 注意：Mod-Enter 可能先触发 blur 再触发 onConfirm，saveHandledRef 防止重复保存
                  flushSave()
                  const latest = localTranslationRef.current
                  const hasTranslation = !!latest
                  const doConfirm = () => {
                    if (!seg.isConfirmed && hasTranslation) {
                      confirmMutation.mutate(undefined, {
                        onSettled: () => {
                          setIsEditing(false)
                        },
                        onSuccess: () => {
                          onConfirmSuccess?.(index)
                        }
                      })
                    } else {
                      // 已确认或无译文：直接退出编辑态，跳转到下一段
                      setIsEditing(false)
                      onConfirmSuccess?.(index)
                    }
                  }
                  // 如果译文有变化，等保存成功后再确认；否则直接确认
                  if (latest !== (seg.translation || '') && hasTranslation) {
                    updateMutation.mutate(buildTranslationPayload(latest), {
                      onSuccess: () => {
                        console.debug('[Segments] 编辑确认：译文已保存，继续确认段落', { segmentId: seg.id })
                        doConfirm()
                      },
                      onError: (err) => {
                        console.error('[Segments] 编辑确认：保存译文失败，未执行确认', { segmentId: seg.id, error: err })
                        toast.error('保存译文失败，请重试')
                      }
                    })
                  } else {
                    doConfirm()
                  }
                }}
                onCancel={() => {
                   localTranslationRef.current = seg.translation || ''
                   setLocalTranslation(seg.translation || '')
                   setIsEditing(false)
                 }}
                 onBlur={handleBlur}
                 autoFocus
                 insertSignal={effectiveInsertSignal}
                 onInsertSignalConsumed={() => {
                   // local 优先，清 local；否则清 global（调用父组件回调）
                   if (localInsertSignal) {
                     setLocalInsertSignal(null)
                   } else {
                     onInsertSignalConsumed?.()
                   }
                 }}
                 initialInsert={pendingInitialInsert}
                 onInitialInsertDone={() => setPendingInitialInsert(null)}
               />
            ) : (
              <>
                {/* TM 100% 匹配标记：显著区分翻译记忆匹配与机翻 */}
                {/* 短剧角色名角标：本段台词所属角色（客户确认角色名不翻译，仅作分组提示） */}
                {(isTmMatch || characterName) && (
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    {isTmMatch && (
                      <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-medium gap-1 border-primary/30 bg-primary/10 text-primary">
                        <BookOpen className="h-2.5 w-2.5" />
                        TM 100% 匹配
                      </Badge>
                    )}
                    {characterName && (
                      <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-medium gap-1 border-secondary/40 bg-secondary/10 text-secondary-foreground">
                        <User className="h-2.5 w-2.5" />
                        {characterName}
                      </Badge>
                    )}
                  </div>
                )}
                <div
                  className={`text-base leading-relaxed whitespace-pre-wrap min-h-[1.5rem] p-1 rounded transition-colors border border-transparent ${
                    !localTranslation ? 'italic text-muted-foreground' : ''
                  } ${
                    canEdit ? 'cursor-text hover:bg-muted/50 text-foreground' : 'text-muted-foreground cursor-not-allowed'
                  } ${
                    seg.isLocked ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => {
                    if (canEdit) {
                      saveHandledRef.current = false  // 进入编辑模式时重置保存标记
                      setIsEditing(true)
                    }
                  }}
                >
                  {localTranslation ? (
                    // 有客户修订差异时，inline 展示 Word 修订模式（绿色新增/红色删除）
                    seg.previousTranslation && seg.previousTranslation.trim() !== localTranslation.trim()
                      ? renderRevisionDiff(seg.previousTranslation, localTranslation)
                      // 无修订差异时，用 TermHighlighter 渲染译文，匹配目标语言术语并支持下划线 + hover 浮层
                      : (terms && terms.length > 0
                        ? <TermHighlighter text={localTranslation} terms={terms} matchField="target" />
                        : renderTextWithTags(localTranslation))
                  ) : (seg.isLocked ? '已锁定' : '点击输入译文...')}
                </div>
              </>
            )}

            {/* 修订备注：仅在有差异的段落下以小字内联展示，不占额外大块 */}
            {seg.revisionNote && seg.previousTranslation && (
              <div className="flex items-start gap-1 text-[10px] text-warning/80 italic">
                <MessageSquare className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" />
                <span>{seg.revisionNote}</span>
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-4">
              {seg.confidenceScore !== null && seg.confidenceScore !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="h-1 w-16 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${seg.confidenceScore > 0.8 ? 'bg-success' : seg.confidenceScore > 0.6 ? 'bg-warning' : 'bg-destructive'}`}
                      style={{ width: `${seg.confidenceScore * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    置信度: {Math.round(seg.confidenceScore * 100)}%
                  </span>
                </div>
              )}
              
              {seg.mqmScores && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal border-info/30 bg-info/10 text-black flex items-center gap-1 cursor-help">
                        <Info className="h-2.5 w-2.5" />
                        MQM: {(() => {
                          try {
                            const scores = typeof seg.mqmScores === 'string' ? JSON.parse(seg.mqmScores) : seg.mqmScores;
                            return Math.round((scores.overallScore || 0) * 100);
                          } catch (e) { return 0; }
                        })()}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-[10px]">
                      {(() => {
                        try {
                          const scores = typeof seg.mqmScores === 'string' ? JSON.parse(seg.mqmScores) : seg.mqmScores;
                          return (
                            <div className="space-y-1">
                              <p className="font-semibold border-b pb-1 mb-1">质量分析</p>
                              {scores.errors?.map((err: any, i: number) => (
                                <div key={i} className="flex justify-between gap-4">
                                  <span>{err.type} ({err.severity})</span>
                                  <span className="text-muted-foreground">{err.count}</span>
                                </div>
                              )) || <p>未发现严重错误</p>}
                            </div>
                          );
                        } catch (e) { return '解析错误'; }
                      })()}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {seg.aiNote && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm text-[11px] whitespace-pre-wrap">
                      <div className="space-y-1">
                        <p className="font-semibold border-b pb-1 mb-1 flex items-center gap-1">
                          <Info className="h-3 w-3" /> AI 思考过程
                        </p>
                        {seg.aiNote}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        )}
      </TableCell>
      <TableCell className="w-[100px] text-right pr-6 align-top pt-4">
        <div className="flex justify-end items-center gap-1">
          {/* 锁定/解锁按钮：始终显示，未锁定状态弱化以便与已锁定区分 */}
          {['translating', 'reviewing', 'error'].includes(projectStatus) && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 transition-opacity ${seg.isLocked ? 'opacity-100' : 'opacity-40 hover:opacity-100'}`}
              onClick={() => {
                console.debug('[Segments] 切换锁定：', { segmentId: seg.id, locked: !seg.isLocked })
                lockMutation.mutate(!seg.isLocked)
              }}
              disabled={lockMutation.isPending}
              title={seg.isLocked ? '解锁段落' : '锁定段落'}
            >
              {lockMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : seg.isLocked ? (
                <svg className="h-3.5 w-3.5 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5 text-muted-foreground/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
            </Button>
          )}

          {/* 重复原文同步开关：仅本项目内完全重复的段落显示，默认参与同步（Link2），点击切为不跟进（Unlink） */}
          {hasDuplicate && ['translating', 'reviewing', 'error'].includes(projectStatus) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 w-7 p-0 transition-opacity ${seg.syncDisabled ? 'opacity-100' : 'opacity-40 hover:opacity-100'}`}
                    onClick={() => {
                      console.debug('[Segments] 切换同步：', { segmentId: seg.id, syncDisabled: !seg.syncDisabled })
                      syncToggleMutation.mutate()
                    }}
                    disabled={syncToggleMutation.isPending}
                  >
                    {syncToggleMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : seg.syncDisabled ? (
                      <Unlink className="h-3.5 w-3.5 text-warning" />
                    ) : (
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground/70" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-[10px]">
                    {seg.syncDisabled
                      ? '本段不跟进重复原文同步（点击开启）'
                      : '重复原文将自动同步译文（点击关闭）'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {seg.isConfirmed ? (
            <CheckCircle2 className="h-5 w-5 text-success" />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-2 text-[10px] transition-opacity ${seg.translation ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              disabled={!seg.translation || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : '确认'}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export function ProjectDetail() {
  const { projectId } = useParams({ from: '/projects/$projectId' })
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [focusIndex, setFocusIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  // 查找替换浮层开关：Cmd+F / Cmd+H 触发，或点击表头"查找替换"按钮
  const [showSearchReplace, setShowSearchReplace] = useState(false)
  // 「只看未确认段落」筛选：审校进度停滞（如 99%）时，开启可快速定位仍未确认的段落
  const [showUnconfirmedOnly, setShowUnconfirmedOnly] = useState(false)
  // Concordance 检索面板开关：Cmd+Shift+F 触发，或点击表头"检索"按钮
  const [showConcordance, setShowConcordance] = useState(false)
  // 全局插入信号：Concordance 面板点"插入"时，把文本插入到当前正在编辑的段落编辑器光标位置。
  // 格式 {segmentId, text, nonce}，nonce 变化触发 SegmentEditor 的插入 effect。
  const [globalInsertSignal, setGlobalInsertSignal] = useState<{
    segmentId: string
    text: string
    nonce: number
  } | null>(null)
  // 当前正在编辑的段落 ID：SegmentRow 进入/退出编辑时上报，Concordance 据此判断可否插入
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null)
  const [ragHintState, setRagHintState] = useState<{
    visible: boolean
    sourceText: string
    position: { x: number; y: number }
  }>({ visible: false, sourceText: '', position: { x: 0, y: 0 } })
  
  const { data: projectResponse, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getById(projectId),
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.data?.status
      return (status === 'translating' || status === 'pending') ? 5000 : false
    }
  })

  const project = projectResponse?.data as Project | undefined

  // segmentsLastUpdatedAt：上次成功获取段落时的 maxUpdatedAt（来自后端），
  // 用于变更预检——下次轮询带 sinceMs=segmentsLastUpdatedAt，未变化后端返回 unchanged=true
  // 前端识别后跳过缓存替换，把全量段落查询降到 MAX(updated_at) 单行查询
  // segmentsResponseRef：缓存最近一次成功的 segments 响应，unchanged=true 时复用避免空数据
  const segmentsLastUpdatedAtRef = useRef<number | undefined>(undefined)
  const segmentsResponseRef = useRef<any>(undefined)
  const { data: segmentsResponse, isLoading: segmentsLoading, refetch: refetchSegments } = useQuery({
    queryKey: ['segments', projectId],
    queryFn: async () => {
      const resp = await segmentsApi.getByProject(projectId, segmentsLastUpdatedAtRef.current)
      // 变更预检：unchanged=true 表示无变化，保留旧缓存（返回旧 data 给 react-query）
      if ((resp as any)?.unchanged) {
        // 抛 stale 状态让 react-query 保留上一次的 data，不触发渲染更新
        return segmentsResponseRef.current || { success: true, data: [] }
      }
      // 正常返回：更新 maxUpdatedAt 供下次轮询使用
      if (resp?.maxUpdatedAt) {
        segmentsLastUpdatedAtRef.current = resp.maxUpdatedAt
      }
      // 缓存最新成功的 response，供下次 unchanged 时复用
      segmentsResponseRef.current = resp
      return resp
    },
    // 轮询策略：所有 mutate 操作（保存段落/确认/启动翻译）都会手动 invalidate，
    // 轮询只是兜底。机翻进行中需实时看进度（10s 间隔 + 变更预检，平衡实时性与 D1 读取量）；
    // 审校中译者手动编辑会立即刷新，30s 兜底足够；qc/pending 等阶段段落不会自动变化，关闭轮询。
    // 间隔从 5s 调整为 10s：变更预检会让多数轮询变成 MAX(updated_at) 轻量查询，
    // 即便 10s 间隔机翻进度延迟也可接受（LLM 一段翻译通常 5-15s）
    refetchInterval: () => {
      const status = project?.status || ''
      if (status === 'translating') return 10000
      if (status === 'reviewing') return 30000
      return false
    }
  })

  // 查询客户全量术语（与 RAG 提示、Concordance 面板数据源统一）
  // 不用 termsApi.lookup（按语言对严格相等过滤，术语入库语言代码不规范时会查不到导致不高亮）
  // 改用 getByClientId 全量查询，前端按项目语言对排序，匹配的优先用于高亮
  const { data: termsResponse } = useQuery({
    queryKey: ['terms', 'client', project?.clientId],
    queryFn: () => {
      if (!project?.clientId) {
        return { success: true, data: [] }
      }
      return termsApi.getByClientId(project.clientId)
    },
    enabled: !!project?.clientId
  })

  // 全量术语，按项目语言对排序（匹配的排前面，不匹配的排后面但仍可用于高亮）
  const terms = useMemo(() => {
    const all = (termsResponse as any)?.data || []
    const sLang = project?.sourceLanguage
    const tLang = project?.targetLanguage
    if (!sLang || !tLang) return all
    return [...all].sort((a: any, b: any) => {
      const aMatch = (a.sourceLang === sLang && a.targetLang === tLang) ? 0 : 1
      const bMatch = (b.sourceLang === sLang && b.targetLang === tLang) ? 0 : 1
      return aMatch - bMatch
    })
  }, [termsResponse, project?.sourceLanguage, project?.targetLanguage])

  // 当项目状态从 translating 变为其他状态时，执行一次最终刷新
  useEffect(() => {
    if (project?.status && project.status !== 'translating' && project.status !== 'pending') {
      refetchSegments()
    }
  }, [project?.status, refetchSegments])

  const startTranslation = useMutation({
    mutationFn: () => projectsApi.startTranslation(projectId),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      const tmLockedCount = res.tmLockedCount || 0
      if (tmLockedCount > 0) {
        toast.success(`AI 翻译已启动，已从翻译记忆库锁定 ${tmLockedCount} 个100%匹配段落`)
      } else {
        toast.success('AI 翻译已启动')
      }
    },
    onError: (err: any) => {
      if (err.needsReferenceConfirmation) {
        toast.error('请先确认参考信息一览表后再启动翻译')
      } else if (err.needsReferenceMatchesConfirmation) {
        toast.error('请先确认参考文献精确匹配预览后再启动翻译')
      } else if (err.needsTermReview) {
        toast.error(`有 ${err.pendingTermCount} 个术语待审核，请先完成术语审核后再启动机翻`)
      } else {
        toast.error(`启动失败: ${err.error || err.message || '未知错误'}`)
      }
    }
  })

  const stopTranslation = useMutation({
    mutationFn: () => projectsApi.stopTranslation(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('翻译已停止')
    }
  })

  const completeReview = useMutation({
    mutationFn: () => projectsApi.completeReview(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('审校已完成，项目已移交至质检阶段')
    },
    onError: (err: any) => {
      // 后端 400 校验失败返回 { success:false, error:"..." }；其他异常可能是 Error 对象
      toast.error('操作失败: ' + (err?.error || err?.message || '未知错误'))
    }
  })

  const completeQC = useMutation({
    mutationFn: () => projectsApi.completeQC(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('质检已完成')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (err: any) => {
      toast.error('操作失败: ' + (err.message || '未知错误'))
    }
  })

  const reopenProject = useMutation({
    mutationFn: () => projectsApi.reopen(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('项目已重新开放编辑')
    },
    onError: (err: any) => {
      toast.error('操作失败: ' + (err.message || '未知错误'))
    }
  })

  const importRevision = useMutation({
    mutationFn: (file: File) => projectsApi.importRevision(projectId, file),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      toast.success(`修订导入成功，已更新 ${res.data?.updatedCount || 0} 条译文`)
    },
    onError: (err: any) => {
      const errorMessage = typeof err === 'string' ? err : err.error || err.message || '未知错误'
      toast.error('导入失败: ' + errorMessage)
    }
  })

  // 短剧（full + dialogue）统一按 intake 的 isLocked 标记过滤：人物名行/非台词段/
  // 人物清单行/独立【】画面字（isLocked=true 且无译文）不在列表显示，仅显示需要翻译
  // 供译者分配的段。录用「isLocked && 无译文」口径，与后端「译者分配」totalSegments、
  // crud.ts 进度、export/translation 排除逻辑完全一致，保证详情页可见段数 = 弹窗总段落。
  // 不再使用人物名行特征判定作兜底：仅台词模式下短台词（居中+短+无标点）会被误判为
  // 人物名行而隐藏，导致详情页段数远小于弹窗。intake 已对人物名行等标记 isLocked，
  // isLocked 即权威口径，全链路统一按它过滤。人物名行仍保留在库中供导出时段落下标对应，
  // 角色名角标仍由 characterNameMap（下方遍历原始段落）独立识别展示。
  // 注意：isLocked 同时被 TM 100% 匹配段复用（锁定且有译文 + confidence 1.0），
  // 须用"有译文"区分——TM 匹配的台词段照常显示（带"TM 100% 匹配"徽章），不能被隐藏。
  const isDrama = project?.documentType === 'drama'
  const rawSegments = (segmentsResponse?.data || []) as Segment[]

  const segments = rawSegments.filter(seg => {
    return !isDrama || !seg.isLocked || !!seg.translation
  })

  // 短剧角色名角标：遍历原始段落（含被隐藏的人物名行），每个台词段记录
  // 其上方最近的人物名行角色（人物名行重置 currentName），台词段在译文区
  // 左上角显示角色名角标，帮助译者区分不同角色的台词块。
  const characterNameMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!isDrama) return map
    let currentName: string | null = null
    for (const seg of rawSegments) {
      const alignment = segFormatAlignment(seg)
      if (isCharacterNameLine(seg.sourceText, alignment)) {
        currentName = cleanCharacterName(seg.sourceText)
      } else if (currentName && String(alignment).toUpperCase().includes('CENTER')) {
        map.set(seg.id, currentName)
      }
    }
    return map
  }, [rawSegments, isDrama])

  // 「只看未确认」筛选衍生列表：开启时隐藏已确认段落，专用于定位审校进度停滞时的残留未确认段。
  // 进度/统计仍基于全量 segments（保持一致口径），仅行渲染与虚拟滚动手游走 displaySegments。
  const displaySegments = showUnconfirmedOnly ? segments.filter(s => !s.isConfirmed) : segments

  // 项目内完全重复段落判定：统计每条原文出现次数，>1 即视为重复段落
  // 用于决定 SegmentRow 是否显示"重复原文同步"标记（小链条图标），避免在所有段落上误显示
  const duplicateSourceTexts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const seg of segments) {
      const text = seg.sourceText
      counts.set(text, (counts.get(text) || 0) + 1)
    }
    const dupSet = new Set<string>()
    for (const [text, count] of counts) {
      if (count > 1) dupSet.add(text)
    }
    return dupSet
  }, [segments])

  // 批量更新段落（查找替换的"全部替换"复用此 mutation）
  const batchUpdateMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; translation: string; isConfirmed: boolean }>) =>
      segmentsApi.batchUpdate(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments', projectId] })
      toast.success('替换完成')
    },
    onError: () => {
      toast.error('替换失败，请重试')
    }
  })

  // 全局快捷键：Cmd+F / Cmd+H 打开查找替换浮层，Cmd+Shift+F 打开检索面板（仅编辑状态下生效）
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F' || e.key === 'h' || e.key === 'H')) {
        // 仅在项目可编辑状态下触发，避免在 pending 等状态下误触
        const status = project?.status || ''
        if (['translating', 'reviewing', 'error'].includes(status)) {
          e.preventDefault()
          // Cmd+Shift+F 打开 Concordance 检索面板（仅 f/F 触发，h/H 仍走查找替换）
          if (e.shiftKey && (e.key === 'f' || e.key === 'F')) {
            setShowConcordance(true)
          } else {
            setShowSearchReplace(true)
          }
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [project?.status])

  const rowVirtualizer = useVirtualizer({
    count: displaySegments.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 80,
    overscan: 5,
  })

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      importRevision.mutate(file)
    }
    // 重置 input，允许重复上传同一文件
    e.target.value = ''
  }

  /** 异步导出：POST 触发 → 轮询 status → GET download，解决 CF 524 超时 */
  const handleExport = async (format: 'original' | 'bilingual' | 'bilingual-docx') => {
    const type = format === 'bilingual' ? 'bilingual' : format === 'bilingual-docx' ? 'bilingual-docx' : 'final';
    const loadingLabel = type === 'final' ? '最终文件' : type === 'bilingual-docx' ? '段段对照 Word' : '双语 Excel';
    const toastId = toast.loading(`正在生成${loadingLabel}...`);

    try {
      // 1. 触发异步生成
      const triggerRes = await projectsApi.exportTrigger(projectId, type);
      const taskId = triggerRes.data?.taskId;
      if (!taskId) {
        throw new Error('触发导出失败：未返回 taskId');
      }

      // 2. 轮询状态（2s 间隔，最长 5 分钟）
      const maxAttempts = 150;
      let status = 'pending';
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await projectsApi.exportStatus(projectId, taskId);
        status = statusRes.data?.status || 'pending';
        if (status === 'done') break;
        if (status === 'error') {
          throw new Error(statusRes.data?.error || '生成失败');
        }
      }
      if (status !== 'done') {
        throw new Error('生成超时，请重试');
      }

      // 3. 下载文件：API 返回 presigned URL，浏览器直连 R2 下载（跳过 Workers 代理，支持断点续传）
      const downloadRes = await projectsApi.exportDownload(projectId, taskId, type);
      const { url, filename: presignedFilename } = downloadRes.data || {};
      if (!url || !presignedFilename) {
        throw new Error('生成下载链接失败');
      }
      // 用 <a download> 触发浏览器原生下载：
      // 跨域时 download 属性会被忽略，但 R2 presigned URL 已通过 ResponseContentDisposition
      // 强制指定文件名，浏览器会用 Content-Disposition 中的文件名（兼容中文）
      const link = document.createElement('a');
      link.href = url;
      link.download = presignedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('文件已开始下载', { id: toastId });
    } catch (err: any) {
      // axios 超时错误 message 为 "timeout of XXXms exceeded"，网络中断为 "Network Error"
      // 区分提示，便于用户判断是网络问题还是服务问题
      const errMessage = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')
        ? '下载超时，请检查网络后重试'
        : err?.message === 'Network Error'
          ? '网络中断，请检查网络后重试'
          : (err?.error || err?.message || '未知错误');
      toast.error(`导出失败: ${errMessage}`, { id: toastId });
    }
  }

  /** 下载源文件：获取 presigned URL 后浏览器直连 R2 下载（所有人可用，便于译者接单后参照源文件） */
  const handleDownloadSource = async () => {
    const toastId = toast.loading('正在获取源文件...');
    try {
      const res = await projectsApi.sourceDownload(projectId);
      const { url, filename } = res.data || {};
      if (!url || !filename) {
        throw new Error('获取下载链接失败');
      }
      // 跨域时 download 属性会被忽略，但 R2 presigned URL 已通过 ResponseContentDisposition
      // 强制指定文件名（兼容中文），浏览器会用 Content-Disposition 中的文件名
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('源文件已开始下载', { id: toastId });
    } catch (err: any) {
      const errMessage = err?.error || err?.message || '未知错误';
      toast.error(`下载源文件失败: ${errMessage}`, { id: toastId });
    }
  }

  if (projectLoading || segmentsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground animate-pulse">正在加载项目数据...</p>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 text-center">
        <div className="bg-muted p-4 rounded-full">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">未找到项目</h3>
          <p className="text-muted-foreground">该项目可能已被删除或您没有权限访问。</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/projects">返回项目列表</Link>
        </Button>
      </div>
    )
  }

  const confirmedCount = segments.filter(s => s.isConfirmed).length
  const progress = segments.length > 0 ? Math.round((confirmedCount / segments.length) * 100) : 0
  // AI 翻译进度：有非空译文，或译者已确认处理（译者有意留空并确认的段落不卡进度条）
  // 注意不能用 truthy 判定：空字符串会被漏算、纯空格会被误算，必须 trim 后判空
  const processedCount = segments.filter(s => (s.translation && s.translation.trim() !== '') || s.isConfirmed).length
  const translationProgress = segments.length > 0 ? Math.round((processedCount / segments.length) * 100) : 0

  const handleConfirmSuccess = (currentIndex: number) => {
    // 找到下一个未确认且有译文的段落，或者仅仅是下一个段落
    const nextUnconfirmedIndex = displaySegments.findIndex((s, i) => i > currentIndex && !s.isConfirmed);
    if (nextUnconfirmedIndex !== -1) {
      setFocusIndex(nextUnconfirmedIndex);
    } else {
      // 如果后面没有未确认的了，就不跳转或者回到顶部寻找
      setFocusIndex(null);
    }
  }

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild className="-ml-2">
            <Link to="/projects">
              <ChevronLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight">{project.fileName}</h1>
              <Badge variant={
                project.status === 'translating' ? 'secondary' :
                project.status === 'reviewing' ? 'outline' :
                project.status === 'qc' ? 'secondary' :
                project.status === 'completed' ? 'default' : 'outline'
              } className={
                project.status === 'reviewing' ? 'bg-warning/10 text-warning-foreground border-warning/30' :
                project.status === 'qc' ? 'bg-info/10 text-black border-info/30' :
                project.status === 'completed' ? 'bg-success text-success-foreground' : ''
              }>
                {
                  project.status === 'pending' ? '待处理' :
                  project.status === 'translating' ? 'AI 翻译中' :
                  project.status === 'reviewing' ? '审校中' :
                  project.status === 'qc' ? '待质检' :
                  project.status === 'completed' ? '已完成' :
                  project.status === 'failed' ? '已失败' : project.status
                }
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {project.client?.name}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                词数: {project.sourceWordCount ?? project.rawWordCount}
              </span>
              {project.documentType && (
                <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">
                  {project.documentType === 'general' ? '媒体稿件' :
                   project.documentType === 'subtitle' ? '字幕' :
                   project.documentType === 'marketing' ? '市场文案' :
                   project.documentType === 'drama' ? '短剧' :
                   project.documentType}
                </span>
              )}
              <span>{formatLanguagePair(project.sourceLanguage, project.targetLanguage)}</span>
              {project.deadline && (
                <span>截止日期: {formatDate(project.deadline)}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          {(project.status === 'pending' || project.status === 'error') && (
            <Button 
              size="sm"
              className="flex-1 md:flex-none"
              onClick={() => startTranslation.mutate()} 
              disabled={startTranslation.isPending || (project.referenceFiles && project.referenceFiles.length > 0 && !project.referenceConfirmed)}
            >
              <Play className="mr-2 h-3.5 w-3.5" /> 
              {startTranslation.isPending 
                ? '启动中...' 
                : project.status === 'error' 
                  ? '重新启动 AI 翻译' 
                  : project.referenceFiles && project.referenceFiles.length > 0 && !project.referenceConfirmed
                    ? '请先确认参考信息'
                    : '启动 AI 翻译'}
            </Button>
          )}
          
          {project.status === 'translating' && (
            <Button 
              size="sm"
              variant="destructive"
              className="flex-1 md:flex-none"
              onClick={() => stopTranslation.mutate()} 
              disabled={stopTranslation.isPending}
            >
              <Square className="mr-2 h-3.5 w-3.5" /> 
              {stopTranslation.isPending ? '停止中...' : '停止翻译'}
            </Button>
          )}
          
          <div className="flex gap-1 flex-1 md:flex-none">
            {/* 下载源文件：所有人可用（含译者），仅项目详情页提供 */}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 md:flex-none"
              onClick={handleDownloadSource}
            >
              <Download className="mr-2 h-3.5 w-3.5" /> 下载源文件
            </Button>
            {(user?.role === 'founder' || user?.role === 'pm') && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 md:flex-none"
                  onClick={handleImportClick}
                  disabled={project.status !== 'completed' || importRevision.isPending}
                >
                  {importRevision.isPending ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-3.5 w-3.5" />
                  )}
                  导入修订
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 md:flex-none"
                  onClick={() => handleExport('bilingual')}
                  disabled={project.status !== 'completed'}
                >
                  <Download className="mr-2 h-3.5 w-3.5" /> 双语对照
                </Button>
              </>
            )}
            {/* 短剧类型 final 直接生成段段对照，不再单独提供段段对照按钮 */}
            {project.fileFormat === 'docx' && project.documentType !== 'drama' && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 md:flex-none"
                onClick={() => handleExport('bilingual-docx')}
                disabled={project.status !== 'completed'}
              >
                <FileDown className="mr-2 h-3.5 h-3.5" /> 段段对照
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 md:flex-none"
              onClick={() => handleExport('original')}
              disabled={!['reviewing', 'qc', 'completed'].includes(project.status)}
            >
              <FileDown className="mr-2 h-3.5 w-3.5" /> 最终文件
            </Button>
            {project.status === 'completed' && (user?.role === 'founder' || user?.role === 'pm') && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 md:flex-none"
                onClick={() => reopenProject.mutate()}
                disabled={reopenProject.isPending}
              >
                {reopenProject.isPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Edit className="mr-2 h-3.5 w-3.5" />
                )}
                重新编辑
              </Button>
            )}
          </div>
        </div>
      </div>

      {project.errorMessage && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-3 text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">
              {project.errorMessage.includes('429') || project.errorMessage.toLowerCase().includes('rate limit')
                ? 'API 速率限制（429）'
                : '处理出错'}
            </p>
            <p className="text-xs opacity-90 mt-1 whitespace-pre-wrap">{project.errorMessage}</p>
          </div>
        </div>
      )}

      {/* 参考信息一览表 */}
      {project.referenceFiles && project.referenceFiles.length > 0 && (
        <ReferenceInfoTable
          projectId={projectId}
          referenceFiles={project.referenceFiles}
          referenceConfirmed={project.referenceConfirmed || false}
          canConfirm={user?.role === 'founder' || user?.role === 'pm'}
        />
      )}

      {/* 参考文献精确匹配 PM 确认（独立步骤，启动机翻前必过） */}
      {project.referenceFiles && project.referenceFiles.length > 0 && (user?.role === 'founder' || user?.role === 'pm') && (
        <ReferenceMatchConfirmCard
          projectId={projectId}
          referenceConfirmed={project.referenceConfirmed || false}
          matchesConfirmed={project.referenceMatchesConfirmed || false}
        />
      )}

      {/* 术语审核面板 */}
      {project.status === 'pending' && (
        <TermReviewPanel 
          projectId={projectId}
          onReviewComplete={() => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] })
          }}
        />
      )}

      {/* 译者分配：PM/founder 可操作，译者只读查看分配信息 */}
      <TranslatorAssignment
        projectId={projectId}
        sourceLanguage={project.sourceLanguage}
        targetLanguage={project.targetLanguage}
        readOnly={user?.role === 'translator'}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="py-2 px-4 flex flex-row items-center justify-between space-y-0">
            <CardDescription className="text-[10px]">AI 翻译进度</CardDescription>
            <span className="text-xs font-bold">{translationProgress}%</span>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-2">
            <div className="h-1 w-full bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-info transition-all duration-500"
                style={{ width: `${translationProgress}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="py-2 px-4 flex flex-row items-center justify-between space-y-0">
            <CardDescription className="text-[10px]">关联资源</CardDescription>
            <div className="flex gap-1">
              <Badge variant="outline" className="h-4 px-1 text-[9px] font-normal border-info/30 bg-info/10 text-black">RAG</Badge>
              <Badge variant="outline" className="h-4 px-1 text-[9px] font-normal border-primary/30 bg-primary/10 text-primary">Style</Badge>
            </div>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-2 flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              术语库已就绪
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className={`w-1.5 h-1.5 rounded-full ${project.status === 'pending' ? 'bg-muted-foreground/50' : 'bg-success'}`} />
              风格指南{project.status === 'pending' ? '待匹配' : '已匹配'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 文档导航和内容区域 */}
      <div className="flex gap-4">
        {/* 大纲导航已下线：跳转定位不稳定，由段落列表滚动浏览替代 */}
        <div className="flex-1 min-w-0">
          <Card className="border-none shadow-none bg-transparent">
            <CardContent className="p-0">
              <div className="rounded-md border bg-card">
                  {/* 查找替换 + 检索入口：独立工具栏，避免挤压状态列表头导致文字竖排 */}
                  {['translating', 'reviewing', 'error'].includes(project?.status || '') && (
                    <div className="flex justify-end items-center gap-1 px-4 py-1.5 border-b">
                      <Button
                        variant={showUnconfirmedOnly ? 'default' : 'ghost'}
                        size="sm"
                        className="h-7 px-2 text-sm gap-1"
                        onClick={() => setShowUnconfirmedOnly(v => !v)}
                        title="只看未确认段落：方便定位审校进度停滞时的残留未确认段"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {showUnconfirmedOnly ? '已隐藏已确认段' : '只看未确认'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-sm gap-1"
                        onClick={() => setShowConcordance(true)}
                        title="检索面板：术语 + TM 历史译文 (Cmd+Shift+F)"
                      >
                        <BookOpen className="h-3.5 w-3.5" />
                        检索
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-sm gap-1"
                        onClick={() => setShowSearchReplace(true)}
                        title="查找替换 (Cmd+F)"
                      >
                        <Search className="h-3.5 w-3.5" />
                        查找替换
                      </Button>
                    </div>
                  )}
                  <Table className="table-fixed">
                  <colgroup>
                    <col style={{ width: '60px' }} />
                    <col style={{ width: '45%' }} />
                    <col style={{ width: '45%' }} />
                    <col style={{ width: '100px' }} />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-6">#</TableHead>
                      <TableHead>原文</TableHead>
                      <TableHead>译文</TableHead>
                      <TableHead className="text-right pr-6">状态</TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>
                <div
                  ref={tableContainerRef}
                  className="overflow-auto relative"
                  style={{ height: displaySegments.length > 50 ? 'calc(100vh - 400px)' : 'auto', maxHeight: 'calc(100vh - 400px)' }}
                >
                  {(user?.role === 'translator' || user?.role === 'founder' || user?.role === 'pm') && project?.clientId && (
                    <TermSuggestionPopover
                      projectId={projectId}
                      clientId={project.clientId}
                      containerRef={tableContainerRef}
                    />
                  )}
                  <Table className="table-fixed">
                    {/* colgroup 显式定义列宽，避免虚拟滚动占位行 <td colSpan=4> 干扰 table-fixed 列宽计算 */}
                    <colgroup>
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '45%' }} />
                      <col style={{ width: '45%' }} />
                      <col style={{ width: '100px' }} />
                    </colgroup>
                    <TableBody>
                      {displaySegments.length === 0 && !segmentsLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                            {showUnconfirmedOnly ? '没有未确认的段落' : '未找到段落数据'}
                          </TableCell>
                        </TableRow>
                      ) : displaySegments.length > 50 ? (
                        (() => {
                          // 虚拟滚动：用首尾占位行撑出总虚拟高度，使滚动条能覆盖全部段落
                          // 否则只渲染可见行，表格高度=可见行高度，滚动容器无法继续向下滚动
                          const virtualItems = rowVirtualizer.getVirtualItems()
                          const totalSize = rowVirtualizer.getTotalSize()
                          const firstStart = virtualItems[0]?.start ?? 0
                          const lastEnd = virtualItems[virtualItems.length - 1]?.end ?? 0
                          return (
                            <>
                              {firstStart > 0 && (
                                <tr aria-hidden="true" style={{ height: firstStart }}>
                                  <td colSpan={4} style={{ padding: 0, border: 'none', lineHeight: 0 }} />
                                </tr>
                              )}
                              {virtualItems.map((virtualRow) => {
                                const seg = displaySegments[virtualRow.index]
                                return (
                                  <SegmentRow
                                    key={seg.id}
                                    seg={seg}
                                    index={virtualRow.index}
                                    projectId={projectId}
                                    projectStatus={project.status}
                                    shouldFocus={focusIndex === virtualRow.index}
                                    onConfirmSuccess={handleConfirmSuccess}
                                    onFocusHandled={() => setFocusIndex(null)}
                                    terms={terms}
                                    onRAGHint={setRagHintState}
                                    rowRef={rowVirtualizer.measureElement}
                                    dataIndex={virtualRow.index}
                                    globalInsertSignal={globalInsertSignal}
                                    onInsertSignalConsumed={() => setGlobalInsertSignal(null)}
                                    onEditingChange={setEditingSegmentId}
                                    hasDuplicate={duplicateSourceTexts.has(seg.sourceText)}
                                    characterName={characterNameMap.get(seg.id)}
                                  />
                                )
                              })}
                              {totalSize - lastEnd > 0 && (
                                <tr aria-hidden="true" style={{ height: totalSize - lastEnd }}>
                                  <td colSpan={4} style={{ padding: 0, border: 'none', lineHeight: 0 }} />
                                </tr>
                              )}
                            </>
                          )
                        })()
                      ) : (
                        displaySegments.map((seg, idx) => (
                          <SegmentRow
                            key={seg.id}
                            seg={seg}
                            index={idx}
                            projectId={projectId}
                            projectStatus={project.status}
                            shouldFocus={focusIndex === idx}
                            onConfirmSuccess={handleConfirmSuccess}
                            onFocusHandled={() => setFocusIndex(null)}
                            terms={terms}
                            onRAGHint={setRagHintState}
                            globalInsertSignal={globalInsertSignal}
                            onInsertSignalConsumed={() => setGlobalInsertSignal(null)}
                            onEditingChange={setEditingSegmentId}
                            hasDuplicate={duplicateSourceTexts.has(seg.sourceText)}
                            characterName={characterNameMap.get(seg.id)}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 底部固定进度条 */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-50 py-2 px-6 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="max-w-7xl mx-auto flex items-center gap-6">
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground whitespace-nowrap border-r pr-4">
            <span className="flex items-center gap-1.5">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                <span className="text-xs">⌘</span>Enter
              </kbd>
              确认
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                <span className="text-xs">⌘</span>S
              </kbd>
              保存
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                <span className="text-xs">⌘</span>D
              </kbd>
              跳过
            </span>
          </div>

          <div className="flex-1 flex items-center gap-4">
            <div className="flex-1 space-y-1">
              <div className="flex justify-between text-[10px] font-medium">
                <span className="text-muted-foreground">审校进度 ({confirmedCount}/{segments.length})</span>
                <span className="text-foreground">{progress}%</span>
              </div>
              <div className="h-1 w-full bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-success transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {project.status === 'qc' ? (
              <Button 
                size="sm" 
                className="h-8 px-4 bg-primary hover:bg-primary/80 text-xs gap-1.5"
                disabled={completeQC.isPending}
                onClick={() => completeQC.mutate()}
              >
                {completeQC.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                质检完成
              </Button>
            ) : (project.status === 'reviewing' || project.status === 'translating') && (user?.role === 'founder' || user?.role === 'pm' || user?.role === 'translator') && (
              <Button
                size="sm"
                className="h-8 px-4 bg-primary hover:bg-primary/90 text-xs gap-1.5"
                disabled={progress < 100 || completeReview.isPending}
                onClick={() => completeReview.mutate()}
              >
                {completeReview.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                完成审校并提交
              </Button>
            )}
          </div>
        </div>
      </div>

      {project?.clientId && (
        <RAGHintPopup
          clientId={project.clientId}
          sourceText={ragHintState.sourceText}
          isVisible={ragHintState.visible}
          position={ragHintState.position}
        />
      )}

      {/* 查找替换浮层：fixed 定位，z-50，覆盖在表格右上角 */}
      {showSearchReplace && segments.length > 0 && (
        <SearchReplacePanel
          segments={segments.map(s => ({
            id: s.id,
            sourceText: s.sourceText,
            translation: s.translation,
            isLocked: s.isLocked,
            isConfirmed: s.isConfirmed,
          }))}
          onNavigate={(segmentIndex) => setFocusIndex(segmentIndex)}
          onClose={() => setShowSearchReplace(false)}
          onSubmitReplace={(updates) => {
            batchUpdateMutation.mutate(updates)
          }}
        />
      )}

      {/* Concordance 检索面板：fixed 浮层，术语 + TM 历史译文检索，支持插入到译文编辑器 */}
      {showConcordance && project?.clientId && (
        <ConcordancePanel
          open={showConcordance}
          onClose={() => setShowConcordance(false)}
          clientId={project.clientId}
          sourceLang={project.sourceLanguage}
          targetLang={project.targetLanguage}
          enableTM={!!project.client?.enableTM}
          onInsertToEditor={(text) => {
            // 插入到当前正在编辑的段落；无编辑段落时提示
            if (!editingSegmentId) {
              toast.error('请先点击一个段落的译文区域进入编辑，再插入')
              return
            }
            setGlobalInsertSignal({ segmentId: editingSegmentId, text, nonce: Date.now() })
          }}
        />
      )}
    </div>
  )
}
